// ABOUTME: Post-login carousel that walks the current customer/relation user through unseen published news articles.
// ABOUTME: Snapshots /news/feed (plus the fulfilment's /relationnews/feed for relations) at mount; marks each item seen on first view.

import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    useGetNewsFeedQuery,
    useMarkArticleSeenMutation,
} from "../../../../newapi/global/newsRootSlice";
import { ENV_TYPES } from "../../Sidebar";
import MViewNewsArticle from "./MViewNewsArticle";
import MViewRelationNews from "../MCreateRelationNews/MViewRelationNews";
import { mergeNewsFeeds } from "../../Topbar/views/newsFeedUtils";
import {
    useGetRelationNewsFeedQuery,
    useMarkRelationNewsSeenMutation,
} from "../../../../newapi/warehouse/relationNewsSlice";
import { useGetCustomerLogoQuery } from "../../../../newapi/user/userSlice";
import { selectUserPermission } from "../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../utils/permissions";

// ─── TEMP preview fixtures — flip to false (or delete this whole block) before commit ───
const PREVIEW_NEWS = false;

// const _p = (text) => ({ type: "paragraph", content: text ? [{ type: "text", text }] : [] });
// const _doc = (...paras) => ({ type: "doc", content: paras });
// const _txt = (id, ...paras) => ({ id, type: "text", content: _doc(...paras) });
// const _img = (id, url) => ({ id, type: "image", content: { url } });
// const _bold = (text) => ({ type: "text", marks: [{ type: "bold" }], text });
// const _color = (text, color) => ({ type: "text", marks: [{ type: "textStyle", attrs: { color } }], text });

// const PREVIEW_SHORT = {
//     id: "preview-short",
//     title: "Korte update 23-6-2026",
//     status: "published",
//     headerImage: { url: "https://picsum.photos/seed/stockhub-short/1200/400" },
//     sections: [
//         _txt("s1",
//             _p("Beste klant,"),
//             _p("Dit is een kort nieuwsbericht om te controleren dat de modal netjes in het midden van het scherm centreert wanneer er weinig inhoud is."),
//         ),
//     ],
// };

// const PREVIEW_LONG = {
//     id: "preview-long",
//     title: "Uitgebreide release notes 23-6-2026",
//     status: "published",
//     headerImage: { url: "https://picsum.photos/seed/stockhub-long/1200/420" },
//     sections: [
//         _txt("l1",
//             _p("Beste klant,"),
//             _p("Dit is een lang nieuwsbericht om te controleren dat de modal eerst naar boven uitklapt tot maximaal 20% marge en daarna naar beneden uitklapt en scrollt."),
//         ),
//         _txt("l2",
//             { type: "paragraph", content: [
//                 { type: "text", text: "Vanaf nu houden we je via " },
//                 _bold("Stockhub WMS"),
//                 { type: "text", text: " op de hoogte van nieuwe functionaliteiten en " },
//                 _color("belangrijke updates", "#2563eb"),
//                 { type: "text", text: "." },
//             ] },
//             _p("Afgelopen vrijdag hebben we de volgende verbeteringen live gezet:"),
//             { type: "bulletList", content: [
//                 { type: "listItem", content: [_p("Palletplaatsen tonen nu het historische gebruik per locatietype.")] },
//                 { type: "listItem", content: [_p("WooCommerce-integraties kunnen de productomschrijving laten meeschrijven bij synchronisatie.")] },
//                 { type: "listItem", content: [_p("Zelf aangemaakte productvelden zijn nu zichtbaar op het inslagscherm.")] },
//             ] },
//         ),
//         _img("l3", "https://picsum.photos/seed/stockhub-warehouse/1200/600"),
//         _txt("l4",
//             _p("Vanuit het bestelling zijpaneel kun je nu met de rechtermuisknop een product openen in een nieuw tabblad. Dit maakt sneller schakelen tussen orders en productinformatie mogelijk."),
//             _p("Bij het dupliceren van een bestelling kun je er nu voor kiezen om de nieuwe bestelling automatisch te openen."),
//         ),
//         _txt("l5",
//             _p("We blijven het systeem verbeteren op basis van jullie feedback. Houd dit nieuwskopje in de gaten voor toekomstige updates."),
//             _p("We werken momenteel aan verbeterde rapportages, snellere zoekfuncties en een vernieuwd dashboard."),
//         ),
//         _img("l6", "https://picsum.photos/seed/stockhub-dashboard/1200/520"),
//         _txt("l7",
//             _p("Heb je vragen of suggesties? Neem gerust contact met ons op via de servicedesk."),
//             _p("Met vriendelijke groet,"),
//             _p("Het Stockhub team"),
//         ),
//     ],
// };

const PREVIEW_FEED = [
    // PREVIEW_SHORT, PREVIEW_LONG
];
// ─── end TEMP preview fixtures ───

const MNewsCarousel = () => {
    const token = useSelector((state) => state.auth.token);
    const envType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isCustomerEnv = envType === ENV_TYPES[100];
    const isRelationEnv = envType === ENV_TYPES[200];
    const isReaderEnv = isCustomerEnv || isRelationEnv;

    // The popup is opt-in per usergroup so warehouse floor staff are not interrupted
    // mid-shipment. Mirrors root's own check on /news/feed, including the relation
    // exemption: relations do not process orders, so there is nobody to protect, and
    // relation usergroups have no such privilege to grant.
    const canReceiveNewsPopup = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.NEWS_POPUP_READ])
    );
    let allowedForEnv = isRelationEnv;
    if (isCustomerEnv) {
        allowedForEnv = canReceiveNewsPopup;
    }

    const enabled = PREVIEW_NEWS || (Boolean(token) && isReaderEnv && allowedForEnv);

    const stockhubFeed = useGetNewsFeedQuery(undefined, { skip: !enabled || PREVIEW_NEWS });
    // News the fulfilment centre sends to its relations pops up alongside stockhub's own news.
    const skipWarehouseFeed = !enabled || PREVIEW_NEWS || !isRelationEnv;
    const warehouseFeed = useGetRelationNewsFeedQuery(undefined, { skip: skipWarehouseFeed });
    const { data: customerLogo } = useGetCustomerLogoQuery(undefined, { skip: skipWarehouseFeed });
    const [markSeen] = useMarkArticleSeenMutation();
    const [markWarehouseSeen] = useMarkRelationNewsSeenMutation();

    // Wait for both feeds so the snapshot below is taken once with everything in it.
    const settled = (q) => q.isUninitialized || q.isSuccess || q.isError;
    const feed = settled(stockhubFeed) && settled(warehouseFeed)
        ? mergeNewsFeeds(stockhubFeed.data || [], warehouseFeed.data || []).reverse()
        : undefined;

    const [stack, setStack] = useState([]);
    const [index, setIndex] = useState(0);
    const [dismissed, setDismissed] = useState(false);
    const seenRef = useRef(new Set());

    useEffect(() => {
        if (!enabled) {
            setStack([]);
            setIndex(0);
            setDismissed(false);
            seenRef.current = new Set();
            return;
        }
        const source = PREVIEW_NEWS ? PREVIEW_FEED : feed;
        if (Array.isArray(source) && source.length > 0 && stack.length === 0 && !dismissed) {
            setStack(source);
            setIndex(0);
        }
    }, [enabled, feed, stack.length, dismissed]);

    const current = stack[index];

    useEffect(() => {
        if (!current?.id) return;
        const seenKey = `${current.source}-${current.id}`;
        if (seenRef.current.has(seenKey)) return;
        seenRef.current.add(seenKey);
        if (PREVIEW_NEWS) return;
        const mark = current.source === "warehouse" ? markWarehouseSeen : markSeen;
        mark(current.id).unwrap().catch((err) => {
            console.error("[news] markSeen failed", err);
        });
    }, [current?.id, current?.source, markSeen, markWarehouseSeen]);

    if (!enabled || dismissed || !current) return null;

    const isLast = index + 1 >= stack.length;
    const isFirst = index === 0;

    const handleNext = () => {
        if (isLast) {
            setDismissed(true);
        } else {
            setIndex(index + 1);
        }
    };

    const handleBack = () => {
        if (isFirst) return;
        setIndex(index - 1);
    };

    const handleClose = () => {
        setDismissed(true);
    };

    if (current.source === "warehouse") {
        return (
            <MViewRelationNews
                open={true}
                handleClose={handleClose}
                newsItem={current}
                logoUrl={customerLogo?.logoUrl ?? null}
                onNext={handleNext}
                onBack={isFirst ? undefined : handleBack}
                nextIsLast={isLast}
            />
        );
    }

    return (
        <MViewNewsArticle
            open={true}
            handleClose={handleClose}
            article={current}
            onNext={handleNext}
            onBack={isFirst ? undefined : handleBack}
            nextIsLast={isLast}
        />
    );
};

export default MNewsCarousel;
